import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { PARTS, CATEGORIES } from '../../data/parts';
import * as api from '../../services/api';
import styles from './ReviewQueue.module.css';

export default function ReviewQueue() {
    const { t } = useLanguage();
    const [searchParams, setSearchParams] = useSearchParams();

    const [filter, setFilter] = useState('pending'); // pending, approved, rejected
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [reviewAction, setReviewAction] = useState(null); // 'approve' | 'reject'
    const [rejectReason, setRejectReason] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        loadConfigs();
    }, [filter]);

    useEffect(() => {
        const configId = searchParams.get('id');
        if (configId && configs.length > 0) {
            const config = configs.find(c => c.id === configId);
            if (config) {
                setSelectedConfig(config);
            }
        }
    }, [searchParams, configs]);

    const loadConfigs = async () => {
        setLoading(true);
        try {
            const result = await api.getPendingConfigurations({ status: filter });
            if (result.success) {
                setConfigs(result.data || []);
            }
        } catch (err) {
            console.error('Failed to load configs:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!selectedConfig) return;
        setProcessing(true);
        try {
            const result = await api.approveConfiguration(selectedConfig.id);
            if (result.success) {
                setConfigs(prev => prev.filter(c => c.id !== selectedConfig.id));
                closeModal();
            }
        } catch (err) {
            console.error('Approve failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const handleReject = async () => {
        if (!selectedConfig || !rejectReason.trim()) return;
        setProcessing(true);
        try {
            const result = await api.rejectConfiguration(selectedConfig.id, rejectReason);
            if (result.success) {
                setConfigs(prev => prev.filter(c => c.id !== selectedConfig.id));
                closeModal();
            }
        } catch (err) {
            console.error('Reject failed:', err);
        } finally {
            setProcessing(false);
        }
    };

    const closeModal = () => {
        setSelectedConfig(null);
        setReviewAction(null);
        setRejectReason('');
        setSearchParams({});
    };

    const openReview = (config) => {
        setSelectedConfig(config);
        setSearchParams({ id: config.id });
    };

    const getPartName = (category, partId) => {
        const part = PARTS[category]?.find(p => p.id === partId);
        return part ? (t(part.nameKey) || part.id) : partId;
    };

    const filterTabs = [
        { id: 'pending', label: t('admin.review.pending') || 'Pending', count: null },
        { id: 'approved', label: t('admin.review.approved') || 'Approved', count: null },
        { id: 'rejected', label: t('admin.review.rejected') || 'Rejected', count: null },
    ];

    return (
        <div className={styles.reviewQueue}>
            <h1 className={styles.title}>
                {t('admin.review.title') || 'Review Queue'}
            </h1>

            {/* Filter Tabs */}
            <div className={styles.tabs}>
                {filterTabs.map(tab => (
                    <button
                        key={tab.id}
                        className={`${styles.tab} ${filter === tab.id ? styles.active : ''}`}
                        onClick={() => setFilter(tab.id)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Content */}
            {loading ? (
                <div className={styles.loading}>
                    {t('common.loading') || 'Loading...'}
                </div>
            ) : configs.length === 0 ? (
                <div className={styles.empty}>
                    <p>{t('admin.review.noItems') || 'No items to review'}</p>
                </div>
            ) : (
                <div className={styles.grid}>
                    {configs.map(config => (
                        <div
                            key={config.id}
                            className={styles.card}
                            onClick={() => openReview(config)}
                        >
                            <div className={styles.cardImage}>
                                {config.cover_image_url ? (
                                    <img src={config.cover_image_url} alt={config.title} />
                                ) : (
                                    <div className={styles.noImage}>{t('common.noImage')}</div>
                                )}
                                <span className={`${styles.statusBadge} ${styles[config.status]}`}>
                                    {t(`admin.review.${config.status}`)}
                                </span>
                            </div>
                            <div className={styles.cardContent}>
                                <h3>{config.title}</h3>
                                <p className={styles.author}>
                                    {t('admin.review.by')} {config.user?.nickname || t('common.anonymous')}
                                </p>
                                <p className={styles.date}>
                                    {new Date(config.created_at).toLocaleDateString()}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Review Modal */}
            {selectedConfig && (
                <div className={styles.modalOverlay} onClick={closeModal}>
                    <div className={styles.modal} onClick={e => e.stopPropagation()}>
                        <button className={styles.closeBtn} onClick={closeModal}>×</button>

                        <div className={styles.modalContent}>
                            {/* Media Gallery */}
                            <div className={styles.mediaSection}>
                                <div className={styles.mainImage}>
                                    {selectedConfig.cover_image_url ? (
                                        <img src={selectedConfig.cover_image_url} alt="" />
                                    ) : (
                                        <div className={styles.noImage}>{t('common.noImage')}</div>
                                    )}
                                </div>
                                {selectedConfig.media?.length > 1 && (
                                    <div className={styles.thumbnails}>
                                        {selectedConfig.media.map((m, i) => (
                                            <img key={i} src={m.url || m.thumbnail_url} alt="" />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Info Section */}
                            <div className={styles.infoSection}>
                                <h2>{selectedConfig.title}</h2>
                                <p className={styles.submitter}>
                                    {t('admin.review.submittedBy') || 'Submitted by'}: {selectedConfig.user?.nickname}
                                    <br />
                                    {t('admin.review.submittedOn') || 'on'}: {new Date(selectedConfig.created_at).toLocaleString()}
                                </p>

                                {selectedConfig.description && (
                                    <div className={styles.description}>
                                        <h4>{t('admin.review.description') || 'Description'}</h4>
                                        <p>{selectedConfig.description}</p>
                                    </div>
                                )}

                                <div className={styles.configDetails}>
                                    <h4>{t('admin.review.configuration') || 'Configuration'}</h4>
                                    <ul>
                                        {CATEGORIES.map(cat => {
                                            const value = selectedConfig.config_data?.[cat.id];
                                            if (!value) return null;
                                            if (Array.isArray(value) && value.length === 0) return null;

                                            return (
                                                <li key={cat.id}>
                                                    <span className={styles.catLabel}>{t(cat.nameKey) || cat.id}:</span>
                                                    <span className={styles.catValue}>
                                                        {Array.isArray(value)
                                                            ? value.map(v => getPartName(cat.id, v)).join(', ')
                                                            : getPartName(cat.id, value)
                                                        }
                                                    </span>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <div className={styles.totalPrice}>
                                        {t('admin.review.totalPrice') || 'Total'}: ¥{selectedConfig.total_price?.toLocaleString()}
                                    </div>
                                </div>

                                {/* Review Actions */}
                                {selectedConfig.status === 'pending' && !reviewAction && (
                                    <div className={styles.reviewActions}>
                                        <button
                                            className={styles.approveBtn}
                                            onClick={() => setReviewAction('approve')}
                                        >
                                            {t('admin.review.approve') || 'Approve'}
                                        </button>
                                        <button
                                            className={styles.rejectBtn}
                                            onClick={() => setReviewAction('reject')}
                                        >
                                            {t('admin.review.reject') || 'Reject'}
                                        </button>
                                    </div>
                                )}

                                {/* Approve Confirmation */}
                                {reviewAction === 'approve' && (
                                    <div className={styles.confirmBox}>
                                        <p>{t('admin.review.confirmApprove') || 'Are you sure you want to approve this configuration?'}</p>
                                        <div className={styles.confirmActions}>
                                            <button
                                                className={styles.confirmBtn}
                                                onClick={handleApprove}
                                                disabled={processing}
                                            >
                                                {processing ? '...' : (t('admin.review.confirmYes') || 'Yes, Approve')}
                                            </button>
                                            <button
                                                className={styles.cancelBtn}
                                                onClick={() => setReviewAction(null)}
                                            >
                                                {t('common.cancel') || 'Cancel'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Reject Form */}
                                {reviewAction === 'reject' && (
                                    <div className={styles.rejectForm}>
                                        <label>{t('admin.review.rejectReason') || 'Reason for rejection'}</label>
                                        <textarea
                                            value={rejectReason}
                                            onChange={e => setRejectReason(e.target.value)}
                                            placeholder={t('admin.review.rejectPlaceholder') || 'Please provide a reason...'}
                                            rows={4}
                                        />
                                        <div className={styles.confirmActions}>
                                            <button
                                                className={styles.rejectConfirmBtn}
                                                onClick={handleReject}
                                                disabled={processing || !rejectReason.trim()}
                                            >
                                                {processing ? '...' : (t('admin.review.confirmReject') || 'Reject')}
                                            </button>
                                            <button
                                                className={styles.cancelBtn}
                                                onClick={() => {
                                                    setReviewAction(null);
                                                    setRejectReason('');
                                                }}
                                            >
                                                {t('common.cancel') || 'Cancel'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Show reject reason for rejected items */}
                                {selectedConfig.status === 'rejected' && selectedConfig.reject_reason && (
                                    <div className={styles.rejectedInfo}>
                                        <h4>{t('admin.review.rejectionReason') || 'Rejection Reason'}</h4>
                                        <p>{selectedConfig.reject_reason}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
